import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { RootState, AppDispatch } from "store";
import { updateUserPoints } from "store/slices/userSlice";
import { OrderPayStatus, OrderInfo } from "common/models/types";
import {
  createPointPurchase,
  updatePointOrder,
  completePayment,
} from "api/pointsService";
import { Text, Button, COLOR_SYSTEM } from "components/atoms";
import PremiumBadge from "components/molecules/PremiumBadge";
import { SlideManager } from "components/organisms/CommonUI/SlidePopup/SlideManager";
import PointsChargeSlide from "components/templates/MyPageTemplate/Points/PointsChargeSlide";
import styled from "styled-components";
import { eventLog } from "utils/AmplitudeUtil";
import { LogLevel, log } from "utils/LogUtil";
import type { IMPResponse } from "types/iamport";
import { loadMySubscription } from "store/slices/subscriptionSlice";
import { useSubscriptionReconcile } from "hooks/useSubscriptionReconcile";
import { useSubscriptionActions } from "hooks/useSubscriptionActions";
import { getSubscriptionUiState, formatKstDate } from "utils/subscriptionDisplay";
import { getPremiumRole, ROLE_NAMES } from "common/constants/roles";

const POINT_PRODUCTS = [
  { id: 1, name: "2,000원", price: 2000, basePoints: 200, bonusRate: 0, totalPoints: 200, isRecommended: false, description: "기본 200P (1회 분석 가능)" },
  { id: 2, name: "10,000원", price: 10000, basePoints: 1000, bonusRate: 5, totalPoints: 1050, isRecommended: false, description: "1,050P (5회 분석 가능)" },
  { id: 3, name: "30,000원", price: 30000, basePoints: 3000, bonusRate: 10, totalPoints: 3300, isRecommended: true, description: "3,300P (16회 분석 가능)" },
];

// 미가입 카드 케이스 트리거 (시안 honest_v8). lead=앞단 상황/타깃(볼드 스캐닝)
const AD_CASES = [
  { lead: "다제약물관리 등 외부 현장", rest: "에서, 방대한 약력을 꼼꼼히 검토해야 할 때" },
  { lead: "약 많은 어르신", rest: ", 약국 밖을 나선 뒤 상호작용이 뒤늦게 마음에 걸릴 때" },
  { lead: "여러 병원 처방이 겹치는 환자", rest: ", 놓친 부분이 없는지 확인하고 싶을 때" },
  { lead: "오늘 응대 중 걸렸던 환자", rest: "를, 퇴근 전 차분히 한 번 더 살필 때" },
];

/**
 * 탭1 "현황" — 멤버십(메인, 위) + 포인트(보조, 아래) 위아래 섹션.
 * 스티비식: 각 섹션 = 제목 + 현재 상태 한 줄 + 액션 버튼.
 */
const SubscriptionPointPanel: React.FC = () => {
  const user = useSelector((s: RootState) => s.user.user);
  const userPoints = useSelector((s: RootState) => s.user.user?.userPoints);
  const roles = useSelector((s: RootState) => s.user.user?.roles);
  const isPremium = useSelector((s: RootState) => s.user.isPremiumUser);
  const isPaidPremium = useSelector((s: RootState) => s.user.isPaidPremiumUser);
  const subscription = useSelector((s: RootState) => s.subscription.me);
  const subscriptionHasLoadedOnce = useSelector(
    (s: RootState) => s.subscription.hasLoadedOnce
  );
  const dispatch = useDispatch<AppDispatch>();
  useSubscriptionReconcile();
  const {
    isProcessing: isSubscriptionProcessing,
    openSubscribeSlide,
    handleChangeCard,
    handleCancel,
    handleResume,
    handleRefresh,
  } = useSubscriptionActions();
  const [isPaymentProcessing, setIsPaymentProcessing] = useState(false);

  useEffect(() => {
    try {
      if (window.IMP) window.IMP.init(process.env.REACT_APP_IMP_MERCHANT_CODE || "imp68909306");
    } catch (error) {
      log(LogLevel.ERROR, "IMP 초기화 실패:", error);
    }
  }, []);

  useEffect(() => {
    dispatch(updateUserPoints());
    dispatch(loadMySubscription());
  }, [dispatch]);

  const handleChargeClick = () => {
    eventLog("포인트_충전_버튼_클릭", {});
    SlideManager.show({
      body: React.createElement(PointsChargeSlide, {
        products: POINT_PRODUCTS,
        onSelect: handlePointsCharge,
        loading: isPaymentProcessing,
      }),
      direction: "right",
      width: "500px",
      height: "100vh",
      needCloseButton: false,
      padding: "0px",
    });
  };

  const handlePointsCharge = async (product: any) => {
    if (isPaymentProcessing) return;
    setIsPaymentProcessing(true);
    try {
      const payMerchantId = `ymyd_phai_points_u${user?.id?.substring(0, 8)}_${Date.now()}`;
      let order: OrderInfo = {
        userId: user?.id || "",
        productName: product.name,
        points: product.totalPoints,
        amount: product.price,
        payAmount: product.price,
        payStatus: OrderPayStatus.PENDING,
        payMerchantId: payMerchantId,
      };
      if (process.env.REACT_APP_ENV === "LOCAL") order.payAmount = 100;

      const createOrderRes = await createPointPurchase(order);
      order.id = createOrderRes.id;

      if (product.price === 0) {
        await updatePointOrder({ id: order.id, payStatus: OrderPayStatus.PAID, paidAmount: 0, paidAt: new Date() });
        handleChargeSuccess();
      } else {
        if (!window.IMP) throw new Error("아임포트 결제 모듈이 로드되지 않았습니다.");
        const paymentParams = {
          pg: process.env.REACT_APP_IMP_PG || "danal_tpay",
          pay_method: "card",
          merchant_uid: payMerchantId,
          name: `PHAI 포인트 충전`,
          amount: order.payAmount,
          buyer_email: user?.email,
          buyer_name: user?.name,
          buyer_tel: user?.phone,
        };
        window.IMP.request_pay(paymentParams, async (res: IMPResponse) => {
          try {
            if (!res.success) {
              setIsPaymentProcessing(false);
              alert(`포인트 충전 결제에 실패했습니다.\n${res.error_msg || "확인 후 다시 시도하시거나 고객센터로 연락주십시오."}`);
              return;
            }
            try {
              await completePayment({ orderId: order.id!, impUid: res.imp_uid!, merchantUid: res.merchant_uid! });
              handleChargeSuccess();
            } catch (completeError) {
              log(LogLevel.ERROR, "[handlePointsCharge] 결제 완료 처리 실패", completeError);
              alert("결제는 완료되었으나 포인트 충전 처리 중 오류가 발생했습니다. 고객센터로 문의해주세요.");
              setIsPaymentProcessing(false);
            }
          } catch (error) {
            setIsPaymentProcessing(false);
            alert("결제는 완료되었으나 처리 중 오류가 발생했습니다. 고객센터로 연락주세요.");
          }
        });
      }
    } catch (error) {
      setIsPaymentProcessing(false);
      log(LogLevel.ERROR, "[handlePointsCharge] 포인트 충전 실패", error);
      alert("포인트 충전 처리 중 오류가 발생했습니다. 다시 시도해주세요.");
    }
  };

  const handleChargeSuccess = () => {
    setIsPaymentProcessing(false);
    dispatch(updateUserPoints());
    SlideManager.hide();
    alert("포인트가 성공적으로 충전되었습니다!");
    eventLog("포인트_충전_성공", {});
  };

  const handlePurchasePoints = () => {
    eventLog("포인트_구매_버튼_클릭", {});
    handleChargeClick();
  };

  // ── 멤버십 섹션 상태별 한 줄 + 버튼 (스티비식) ──
  const ui = getSubscriptionUiState(subscription, subscriptionHasLoadedOnce);
  const nextDate = subscription?.nextBillingAt ? formatKstDate(subscription.nextBillingAt) : null;
  const periodEnd = subscription?.currentPeriodEnd ? formatKstDate(subscription.currentPeriodEnd) : null;
  const busy = isSubscriptionProcessing;

  type MembershipView = {
    badge: boolean;
    label?: string;
    meta?: React.ReactNode;
    text: React.ReactNode;
    textType?: "Body1" | "Body1SemiBold" | "H4";
    sub?: React.ReactNode;
    body?: React.ReactNode;
    footnote?: React.ReactNode;
    buttons: React.ReactNode;
    pointsTail?: React.ReactNode;
  };

  const KEEP_POINTS_TAIL = "멤버십 중엔 차감되지 않고 그대로 보관돼요";
  // 구독자 카드 공통 헤드라인+서브 (시안 subscriber_aligned_v3)
  const SUBSCRIBER_HEADLINE = "담당하시는 환자, 마음껏 살펴보세요.";
  const SUBSCRIBER_SUB = "건당 비용 고민 없이, 복잡한 처방도 언제든 종합 분석할 수 있어요.";

  const renderMembership = (): MembershipView => {
    // 약사회·운영 부여 (무제한이지만 유료 구독 아님)
    if (isPremium && !isPaidPremium) {
      const role = getPremiumRole(roles);
      const roleName = role ? ROLE_NAMES[role] ?? role : null;
      return {
        badge: true,
        label: "Premium 이용 중",
        meta: roleName ? `${roleName} 지원` : undefined,
        text: SUBSCRIBER_HEADLINE,
        textType: "Body1SemiBold",
        sub: SUBSCRIBER_SUB,
        buttons: null,
        pointsTail: KEEP_POINTS_TAIL,
      };
    }
    switch (ui.bodyKind) {
      case "loading":
        return { badge: false, text: "확인 중…", buttons: null };
      case "ad":
        return {
          badge: false,
          text: "환자가 먹는 약, 빠짐없이 살펴보세요.",
          textType: "H4",
          sub: "건당 비용 고민 없이, 약사님이 담당하는 환자를 마음껏 챙기세요.",
          body: (
            <>
              <CaseBox>
                <CaseHeader textType="Body2SemiBold" color={COLOR_SYSTEM.get("Gray")[900]}>
                  현장에서든 약국에서든, 이런 환자를 짚어볼 때
                </CaseHeader>
                <CaseList>
                  {AD_CASES.map((c, i) => (
                    <Text key={i} textType="Body2" color={COLOR_SYSTEM.get("Gray")[500]}>
                      · <strong style={{ color: COLOR_SYSTEM.get("Gray")[900] }}>{c.lead}</strong>{c.rest}
                    </Text>
                  ))}
                </CaseList>
              </CaseBox>
              <ExplainBlock>
                <Text textType="Body2" color={COLOR_SYSTEM.get("Gray")[500]}>
                  지나치기 쉬운 상호작용·처방연쇄·용량 문제를 PhAI가 빠짐없이 짚어줍니다. 약사님의 전문적인 직관을 뒷받침하는 확실한 크로스체크 도구로 활용해 보세요.
                </Text>
              </ExplainBlock>
            </>
          ),
          footnote: "환자 1명당 약 10분의 심층 AI 분석이 진행됩니다. 현장의 즉답용보다는, 환자 응대 후 차분하고 꼼꼼하게 검토하는 용도로 권장합니다.",
          buttons: <Button size="Medium" type="Text" variant="Contained" color="Primary" disabled={busy} onClick={openSubscribeSlide}>멤버십 가입</Button>,
        };
      case "pending":
        return {
          badge: false,
          text: "결제 확인 중…",
          buttons: <Button size="Medium" type="Text" variant="Outlined" color="Primary" onClick={handleRefresh}>새로고침</Button>,
        };
      case "active":
        return {
          badge: true,
          label: "Premium 이용 중",
          meta: nextDate ? `다음 결제일 ${nextDate}` : undefined,
          text: SUBSCRIBER_HEADLINE,
          textType: "Body1SemiBold",
          sub: SUBSCRIBER_SUB,
          buttons: (
            <>
              <Button size="Medium" type="Text" variant="Outlined" color="Primary" disabled={busy} onClick={handleChangeCard}>카드 변경</Button>
              <Button size="Medium" type="Text" variant="Outlined" color="Negative" disabled={busy} onClick={handleCancel}>해지하기</Button>
            </>
          ),
          pointsTail: "멤버십 중엔 차감되지 않고 그대로 보관돼요",
        };
      case "cancelling":
        return { badge: false, text: "해지를 처리하는 중…", buttons: null };
      case "cancelled":
        return {
          badge: true,
          label: "Premium 이용 중",
          meta: periodEnd ? `${periodEnd}까지 이용 가능` : undefined,
          text: "해지 예정이에요.",
          sub: (
            <>
              {periodEnd ? (
                <><strong style={{ color: COLOR_SYSTEM.get("Gray")[900] }}>{periodEnd}</strong>까지는 멤버십을 그대로 이용할 수 있어요.</>
              ) : (
                "이용 가능일까지는 멤버십을 그대로 이용할 수 있어요."
              )}
              <br />따로 재개하지 않으면 그 이후 멤버십이 자동으로 종료됩니다.
              <br />'다시 구독'을 누르면 언제든 기존 결제수단으로 멤버십을 이어갈 수 있어요.
            </>
          ),
          buttons: <Button size="Medium" type="Text" variant="Contained" color="Primary" disabled={busy} onClick={handleResume}>다시 구독</Button>,
          pointsTail: KEEP_POINTS_TAIL,
        };
      case "past_due":
        return {
          badge: false,
          text: <><strong style={{ color: COLOR_SYSTEM.get("Red")[600] }}>결제 실패</strong> — 카드를 확인해 주세요</>,
          buttons: <Button size="Medium" type="Text" variant="Contained" color="Primary" disabled={busy} onClick={handleChangeCard}>카드 변경</Button>,
        };
      case "expired":
        return {
          badge: false,
          text: "멤버십이 만료되었어요.",
          buttons: <Button size="Medium" type="Text" variant="Contained" color="Primary" disabled={busy} onClick={openSubscribeSlide}>멤버십 가입</Button>,
        };
      default:
        return { badge: false, text: "", buttons: null };
    }
  };
  const membership = renderMembership();

  return (
    <Container>
      {/* 멤버십 (메인) — 카드로 무게 */}
      <SectionTitle textType="H3" color={COLOR_SYSTEM.get("Gray")[900]}>멤버십</SectionTitle>
      <MembershipCard>
        {(membership.badge || membership.meta) && (
          <BadgeRow>
            {membership.badge ? <PremiumBadge size="small" label={membership.label} /> : <span />}
            {membership.meta && (
              <Text textType="Body2" color={COLOR_SYSTEM.get("Gray")[500]}>{membership.meta}</Text>
            )}
          </BadgeRow>
        )}
        <Headline textType={membership.textType ?? "Body1"} color={COLOR_SYSTEM.get("Gray")[900]}>
          {membership.text}
        </Headline>
        {membership.sub && (
          <Sub textType="Body2" color={COLOR_SYSTEM.get("Gray")[500]}>{membership.sub}</Sub>
        )}
        {membership.body}
        {membership.buttons && <Actions>{membership.buttons}</Actions>}
        {membership.footnote && (
          <Footnote textType="Caption" color={COLOR_SYSTEM.get("Gray")[400]}>{membership.footnote}</Footnote>
        )}
      </MembershipCard>

      {/* 포인트 (보조) */}
      {isPremium ? (
        // 멤버십 이용 중 — 카드 밖 한 줄 (차감 없음 안내)
        <PointsLine>
          <Text textType="Body2" color={COLOR_SYSTEM.get("Gray")[500]}>보유 포인트</Text>
          <Text textType="Body2SemiBold" color={COLOR_SYSTEM.get("Gray")[900]}>
            {userPoints?.currentPoints?.toLocaleString() || "0"}P
          </Text>
          <Text textType="Caption" color={COLOR_SYSTEM.get("Gray")[400]}>
            · {membership.pointsTail ?? KEEP_POINTS_TAIL}
          </Text>
        </PointsLine>
      ) : (
        // 미가입 — 포인트 건별 이용 안내 서브박스 (시안 v9)
        <PointSubBox>
          <PointSubInfo>
            <PointSubTitle textType="Body2" color={COLOR_SYSTEM.get("Gray")[700]}>포인트로 건별 이용</PointSubTitle>
            <PointSubDesc textType="Caption" color={COLOR_SYSTEM.get("Gray")[400]}>
              월 이용량이 적다면, 필요할 때만 포인트를 충전해 사용해 보세요. <span style={{ whiteSpace: "nowrap" }}>(1건당 200P 차감)</span>
            </PointSubDesc>
          </PointSubInfo>
          <PointSubRight>
            <Text textType="Caption" color={COLOR_SYSTEM.get("Gray")[400]}>
              보유 <strong style={{ color: COLOR_SYSTEM.get("Gray")[600] }}>{userPoints?.currentPoints?.toLocaleString() || "0"}P</strong>
            </Text>
            <Button size="Small" type="Text" variant="Outlined" color="Secondary" onClick={handlePurchasePoints}>충전하기</Button>
          </PointSubRight>
        </PointSubBox>
      )}
    </Container>
  );
};

export default SubscriptionPointPanel;

const Container = styled.div`
  padding: 0;
`;

const SectionTitle = styled(Text)`
  display: block;
  margin-bottom: 14px;
`;

const MembershipCard = styled.div`
  background: ${COLOR_SYSTEM.get("Gray")[0]};
  border: 1px solid ${COLOR_SYSTEM.get("Gray")[100]};
  border-radius: 12px;
  padding: 24px;
`;

const BadgeRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
`;

const Headline = styled(Text)`
  display: block;
  line-height: 1.5;
`;

const Sub = styled(Text)`
  display: block;
  margin-top: 8px;
  line-height: 1.6;
`;

// ① 서브타이틀과 여백 2배로 띄우고 ② 케이스를 연회색 박스로 가둠
const CaseBox = styled.div`
  margin-top: 28px;
  padding: 18px 20px;
  background: ${COLOR_SYSTEM.get("Gray")[15]};
  border-radius: 10px;
`;

const CaseHeader = styled(Text)`
  display: block;
  margin-bottom: 13px;
`;

const CaseList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;

  & > * {
    display: block;
    line-height: 1.55;
  }
`;

const ExplainBlock = styled.div`
  margin-top: 22px;
  border-top: 1px solid ${COLOR_SYSTEM.get("Gray")[100]};
  padding-top: 16px;

  & > * {
    display: block;
    line-height: 1.7;
  }
`;

const Footnote = styled(Text)`
  display: block;
  margin-top: 14px;
  line-height: 1.6;
`;

const Actions = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 20px;
`;

const PointsLine = styled.div`
  display: flex;
  align-items: baseline;
  gap: 8px;
  flex-wrap: wrap;
  margin-top: 28px;
`;

const PointSubBox = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
  margin-top: 12px;
  padding: 16px 18px;
  background: ${COLOR_SYSTEM.get("Gray")[10]};
  border-radius: 8px;
`;

const PointSubInfo = styled.div`
  flex: 1;
  min-width: 240px;
`;

const PointSubTitle = styled(Text)`
  display: block;
  margin-bottom: 4px;
`;

const PointSubDesc = styled(Text)`
  display: block;
  line-height: 1.55;
`;

const PointSubRight = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;
